import type { LucideIcon } from 'lucide-react';
import type { ProductId } from '@/lib/constants/products';

/**
 * Search is layered, cheapest first. Each layer is a separate provider behind
 * one interface so a later layer can be added without touching the UI:
 *
 * - `destination` — "where is PTO?" Pure client-side index over PRODUCTS. Instant.
 * - `record` — "find Jane's CPR renewal." Hits the API, one handler per entity.
 * - `assistant` — natural-language filtering over records. Not built.
 *
 * Results carry their layer so the UI can group them and label where an answer
 * came from, and so a slow layer can stream in under an instant one.
 */
export type SearchLayer = 'destination' | 'record' | 'assistant';

export const LAYER_ORDER: SearchLayer[] = ['destination', 'record', 'assistant'];

export interface SearchResult {
  /** Stable within a layer — used as the React key and for dedupe. */
  id: string;
  title: string;
  /** Where this lives, e.g. "Document Hub" or "Document Hub · Setup". */
  subtitle: string;
  href: string;
  icon: LucideIcon;
  layer: SearchLayer;
  /** Owning hub, when there is one. Drives the accent color on the row. */
  hub?: ProductId;
  /**
   * A hub the user could subscribe to but has not. Selecting one opens the hub
   * picker rather than navigating — `href` would 404 through the layout guard.
   * Only ever set for owners, who are the only people able to act on it.
   */
  locked?: boolean;
  /** Higher sorts first. See `scoreEntry` for the scale. */
  score: number;
}

/**
 * Everything a provider needs to decide what this user is allowed to find.
 * Mirrors what the sidebar already knows, so search can reuse the same gates
 * rather than inventing a second, drift-prone set.
 */
export interface SearchContext {
  hasProductAccess: (id: ProductId) => boolean;
  getProductRole: (id: ProductId) => string | null;
  isOwner: boolean;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  /** Some nav items only appear for multi-school accounts. */
  multiSchool: boolean;
}

export interface SearchProvider {
  layer: SearchLayer;
  /** May return a promise — the UI renders each layer as it resolves. */
  search: (query: string, ctx: SearchContext) => SearchResult[] | Promise<SearchResult[]>;
}
